/**
 * Admin Security Settings Page
 * 
 * Features:
 * - User Management: List, invite, edit, deactivate users
 * - Security Restrictions: IP whitelist/blacklist, time/day/geo restrictions
 * - MFA Settings: Email OTP (future: TOTP)
 * - Pending Invitations management
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Users, Mail, Shield, Clock, Globe, Lock, Plus, Search, MoreVertical,
  UserPlus, RefreshCw, Edit2, Trash2, Key, Check, X, Loader2, AlertCircle,
  MapPin, Calendar, Eye, EyeOff, ChevronDown, Upload, User, LogIn
} from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// ==============================================
// API FUNCTIONS
// ==============================================

const securityAPI = {
  // Users
  getUsers: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    if (params.search) queryParams.append('search', params.search);
    if (params.role) queryParams.append('role', params.role);
    if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
    
    const res = await fetch(`${API_BASE_URL}/api/users/admin?${queryParams}`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  },
  
  getUser: async (userId) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch user');
    return res.json();
  },
  
  updateUser: async (userId, data) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to update user');
    }
    return res.json();
  },
  
  deactivateUser: async (userId) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to deactivate user');
    return res.json();
  },
  
  inviteUser: async (data) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/invite`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to invite user');
    }
    return res.json();
  },
  
  resendInvite: async (userId) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}/resend-invite`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to resend invitation');
    return res.json();
  },
  
  // Invitations
  getInvitations: async () => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/invitations`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch invitations');
    return res.json();
  },
  
  cancelInvitation: async (invitationId) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/invitations/${invitationId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to cancel invitation');
    return res.json();
  },
  
  // Security
  getUserSecurity: async (userId) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}/security`, {
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to fetch security settings');
    return res.json();
  },
  
  updateUserSecurity: async (userId, data) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}/security`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update security settings');
    return res.json();
  },
  
  resetMFA: async (userId) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}/reset-mfa`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!res.ok) throw new Error('Failed to reset MFA');
    return res.json();
  },
  
  // Impersonate (Login as User)
  impersonateUser: async (userId) => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}/impersonate`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to impersonate user');
    }
    return res.json();
  },
  
  // End impersonation and return to admin
  endImpersonation: async () => {
    const res = await fetch(`${API_BASE_URL}/api/users/admin/end-impersonation`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to end impersonation');
    }
    return res.json();
  },
  
  // Avatar
  uploadAvatar: async (userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const res = await fetch(`${API_BASE_URL}/api/users/admin/${userId}/avatar`, {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    if (!res.ok) throw new Error('Failed to upload avatar');
    return res.json();
  }
};

// ==============================================
// ROLE DEFINITIONS
// ==============================================

const AVAILABLE_ROLES = [
  { id: 'admin', label: 'Admin', description: 'Full system access', color: 'purple' },
  { id: 'manager', label: 'Manager', description: 'Management access', color: 'blue' },
  { id: 'accounting', label: 'Accounting', description: 'Financial access', color: 'green' },
  { id: 'sales', label: 'Sales', description: 'Sales & CRM access', color: 'amber' },
  { id: 'production', label: 'Production', description: 'Production workflow', color: 'orange' },
  { id: 'office_staff', label: 'Office Staff', description: 'General office access', color: 'slate' },
  { id: 'front_desk', label: 'Front Desk', description: 'Customer-facing', color: 'cyan' },
  { id: 'user', label: 'Staff', description: 'Basic staff access', color: 'gray' },
  { id: 'customer', label: 'Customer', description: 'Customer portal', color: 'emerald' },
];

const getRoleBadgeColor = (role) => {
  const roleConfig = AVAILABLE_ROLES.find(r => r.id === role);
  if (!roleConfig) return 'bg-gray-100 text-gray-700';
  
  const colors = {
    purple: 'bg-purple-100 text-purple-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    amber: 'bg-amber-100 text-amber-700',
    orange: 'bg-orange-100 text-orange-700',
    slate: 'bg-slate-100 text-slate-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    gray: 'bg-gray-100 text-gray-700',
    emerald: 'bg-emerald-100 text-emerald-700',
  };
  return colors[roleConfig.color] || 'bg-gray-100 text-gray-700';
};

// ==============================================
// INVITE USER MODAL
// ==============================================

const InviteUserModal = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    roles: ['user']
  });
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await securityAPI.inviteUser(formData);
      toast.success(`Invitation sent to ${formData.email}`);
      onSuccess?.();
      onClose();
      setFormData({ email: '', first_name: '', last_name: '', roles: ['user'] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleRole = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(r => r !== roleId)
        : [...prev.roles, roleId]
    }));
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Invite New User
          </DialogTitle>
          <DialogDescription>
            Send an invitation email to set up their account.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          
          <div>
            <Label>Roles</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {AVAILABLE_ROLES.filter(r => r.id !== 'customer').map(role => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => toggleRole(role.id)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    formData.roles.includes(role.id)
                      ? getRoleBadgeColor(role.id) + ' border-transparent'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {role.label}
                </button>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ==============================================
// EDIT USER MODAL
// ==============================================

const EditUserModal = ({ user, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    name: '',
    email: '',
    roles: [],
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        name: user.name || '',
        email: user.email || '',
        roles: user.roles || [user.role] || ['user'],
        is_active: user.is_active !== false
      });
    }
  }, [user]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await securityAPI.updateUser(user.user_id, formData);
      toast.success('User updated successfully');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const toggleRole = (roleId) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(roleId)
        ? prev.roles.filter(r => r !== roleId)
        : [...prev.roles, roleId]
    }));
  };
  
  if (!user) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 className="w-5 h-5" />
            Edit User
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit_first_name">First Name</Label>
              <Input
                id="edit_first_name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit_last_name">Last Name</Label>
              <Input
                id="edit_last_name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="edit_email">Email Address</Label>
            <Input
              id="edit_email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          
          <div>
            <Label>Roles</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {AVAILABLE_ROLES.map(role => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => toggleRole(role.id)}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    formData.roles.includes(role.id)
                      ? getRoleBadgeColor(role.id) + ' border-transparent'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {role.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <Label>Account Status</Label>
              <p className="text-sm text-gray-500">
                {formData.is_active ? 'User can log in' : 'User is deactivated'}
              </p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ==============================================
// SECURITY SETTINGS MODAL
// ==============================================

const SecuritySettingsModal = ({ user, open, onClose }) => {
  const [security, setSecurity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    if (open && user) {
      loadSecurity();
    }
  }, [open, user]);
  
  const loadSecurity = async () => {
    setLoading(true);
    try {
      const data = await securityAPI.getUserSecurity(user.user_id);
      setSecurity(data.security_restrictions || {
        ip_whitelist_enabled: false,
        ip_whitelist: [],
        ip_blacklist_enabled: false,
        ip_blacklist: [],
        time_restriction: { enabled: false, start_hour: 8, end_hour: 18 },
        day_restriction: { enabled: false, allowed_days: [0, 1, 2, 3, 4] },
        geo_restriction: { enabled: false, allowed_countries: ['US'] }
      });
    } catch (err) {
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await securityAPI.updateUserSecurity(user.user_id, security);
      toast.success('Security settings updated');
      onClose();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleResetMFA = async () => {
    if (!window.confirm('Are you sure you want to reset MFA for this user?')) return;
    
    try {
      await securityAPI.resetMFA(user.user_id);
      toast.success('MFA has been reset');
    } catch (err) {
      toast.error('Failed to reset MFA');
    }
  };
  
  if (!user) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security Settings - {user.name || user.email}
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : security && (
          <div className="space-y-6">
            {/* IP Whitelist */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">IP Whitelist</span>
                </div>
                <Switch
                  checked={security.ip_whitelist_enabled}
                  onCheckedChange={(checked) => setSecurity({
                    ...security,
                    ip_whitelist_enabled: checked
                  })}
                />
              </div>
              {security.ip_whitelist_enabled && (
                <div>
                  <Label className="text-sm text-gray-500">Allowed IPs (one per line)</Label>
                  <textarea
                    className="w-full mt-1 p-2 border rounded text-sm font-mono"
                    rows={3}
                    value={(security.ip_whitelist || []).join('\n')}
                    onChange={(e) => setSecurity({
                      ...security,
                      ip_whitelist: e.target.value.split('\n').filter(ip => ip.trim())
                    })}
                    placeholder="192.168.1.100&#10;10.0.0.0/24"
                  />
                </div>
              )}
            </div>
            
            {/* IP Blacklist */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">IP Blacklist</span>
                </div>
                <Switch
                  checked={security.ip_blacklist_enabled}
                  onCheckedChange={(checked) => setSecurity({
                    ...security,
                    ip_blacklist_enabled: checked
                  })}
                />
              </div>
              {security.ip_blacklist_enabled && (
                <div>
                  <Label className="text-sm text-gray-500">Blocked IPs (one per line)</Label>
                  <textarea
                    className="w-full mt-1 p-2 border rounded text-sm font-mono"
                    rows={3}
                    value={(security.ip_blacklist || []).join('\n')}
                    onChange={(e) => setSecurity({
                      ...security,
                      ip_blacklist: e.target.value.split('\n').filter(ip => ip.trim())
                    })}
                    placeholder="203.0.113.0/24"
                  />
                </div>
              )}
            </div>
            
            {/* Time Restriction */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">Time Restriction</span>
                </div>
                <Switch
                  checked={security.time_restriction?.enabled}
                  onCheckedChange={(checked) => setSecurity({
                    ...security,
                    time_restriction: { ...security.time_restriction, enabled: checked }
                  })}
                />
              </div>
              {security.time_restriction?.enabled && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Start Hour</Label>
                    <Select
                      value={String(security.time_restriction?.start_hour || 8)}
                      onValueChange={(val) => setSecurity({
                        ...security,
                        time_restriction: { ...security.time_restriction, start_hour: parseInt(val) }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {i.toString().padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">End Hour</Label>
                    <Select
                      value={String(security.time_restriction?.end_hour || 18)}
                      onValueChange={(val) => setSecurity({
                        ...security,
                        time_restriction: { ...security.time_restriction, end_hour: parseInt(val) }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 24 }, (_, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {i.toString().padStart(2, '0')}:00
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
            
            {/* Day Restriction */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">Day Restriction</span>
                </div>
                <Switch
                  checked={security.day_restriction?.enabled}
                  onCheckedChange={(checked) => setSecurity({
                    ...security,
                    day_restriction: { ...security.day_restriction, enabled: checked }
                  })}
                />
              </div>
              {security.day_restriction?.enabled && (
                <div className="flex flex-wrap gap-2">
                  {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, idx) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        const days = security.day_restriction?.allowed_days || [];
                        setSecurity({
                          ...security,
                          day_restriction: {
                            ...security.day_restriction,
                            allowed_days: days.includes(idx)
                              ? days.filter(d => d !== idx)
                              : [...days, idx]
                          }
                        });
                      }}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        (security.day_restriction?.allowed_days || []).includes(idx)
                          ? 'bg-blue-100 text-blue-700 border-blue-200'
                          : 'bg-white text-gray-600 border-gray-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Geographic Restriction */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">Geographic Restriction</span>
                </div>
                <Switch
                  checked={security.geo_restriction?.enabled}
                  onCheckedChange={(checked) => setSecurity({
                    ...security,
                    geo_restriction: { ...security.geo_restriction, enabled: checked }
                  })}
                />
              </div>
              {security.geo_restriction?.enabled && (
                <div>
                  <Label className="text-sm text-gray-500">Allowed Countries (comma-separated codes)</Label>
                  <Input
                    className="mt-1"
                    value={(security.geo_restriction?.allowed_countries || []).join(', ')}
                    onChange={(e) => setSecurity({
                      ...security,
                      geo_restriction: {
                        ...security.geo_restriction,
                        allowed_countries: e.target.value.split(',').map(c => c.trim().toUpperCase()).filter(c => c)
                      }
                    })}
                    placeholder="US, CA, MX"
                  />
                </div>
              )}
            </div>
            
            {/* MFA Reset */}
            <div className="border rounded-lg p-4 bg-red-50 border-red-200">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 font-medium text-red-700">
                    <Key className="w-4 h-4" />
                    Reset MFA
                  </div>
                  <p className="text-sm text-red-600 mt-1">
                    User will need to set up MFA again on next login.
                  </p>
                </div>
                <Button variant="outline" size="sm" className="text-red-600 border-red-300" onClick={handleResetMFA}>
                  Reset
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ==============================================
// MAIN COMPONENT
// ==============================================

const AdminSecuritySettings = () => {
  const [activeTab, setActiveTab] = useState('users');
  
  // Users state
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(true);
  
  // Modals
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [securityUser, setSecurityUser] = useState(null);
  const [viewUser, setViewUser] = useState(null);
  
  // Send password reset handler
  const handleSendPasswordReset = async (user) => {
    if (!user.password_hash) {
      toast.error('This user uses Google login and cannot reset password');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/admin/${user.user_id}/send-password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send reset email');
      }
      
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (err) {
      toast.error(err.message || 'Failed to send password reset');
    }
  };
  
  // Fetch users
  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = { page_size: 100 };
      if (searchQuery) params.search = searchQuery;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active';
      
      const data = await securityAPI.getUsers(params);
      setUsers(data.users || []);
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [searchQuery, roleFilter, statusFilter]);
  
  // Fetch invitations
  const fetchInvitations = useCallback(async () => {
    setInvitationsLoading(true);
    try {
      const data = await securityAPI.getInvitations();
      setInvitations(data.invitations || []);
    } catch (err) {
      console.error('Failed to load invitations:', err);
    } finally {
      setInvitationsLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  useEffect(() => {
    if (activeTab === 'invitations') {
      fetchInvitations();
    }
  }, [activeTab, fetchInvitations]);
  
  const handleDeactivate = async (user) => {
    if (!window.confirm(`Are you sure you want to deactivate ${user.name || user.email}?`)) return;
    
    try {
      await securityAPI.deactivateUser(user.user_id);
      toast.success('User deactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const handleCancelInvitation = async (invitation) => {
    if (!window.confirm(`Cancel invitation for ${invitation.email}?`)) return;
    
    try {
      await securityAPI.cancelInvitation(invitation.id);
      toast.success('Invitation cancelled');
      fetchInvitations();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const handleResendInvitation = async (invitation) => {
    try {
      await securityAPI.resendInvite(invitation.id);
      toast.success(`Invitation resent to ${invitation.email}`);
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  // Handle Login as User (Impersonation)
  const handleImpersonate = async (user) => {
    if (!window.confirm(`Login as ${user.name || user.email}? You will be logged in as this user.`)) return;
    
    try {
      const result = await securityAPI.impersonateUser(user.user_id);
      
      // Store impersonation info in localStorage for the banner
      localStorage.setItem('impersonation', JSON.stringify({
        impersonatedBy: result.impersonated_by,
        targetUser: result.user,
        isImpersonation: true
      }));
      
      toast.success(`Now logged in as ${result.user.name || result.user.email}`);
      
      // Reload the page - the session cookie has been updated by the backend
      setTimeout(() => {
        window.location.href = '/admin';
      }, 500);
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="admin-security-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">Security & Users</h2>
          <p className="text-sm text-gray-500">Manage users, roles, and security settings</p>
        </div>
        <Button onClick={() => setInviteModalOpen(true)} data-testid="invite-user-btn">
          <UserPlus className="w-4 h-4 mr-2" />
          Invite User
        </Button>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Invitations ({invitations.length})
          </TabsTrigger>
        </TabsList>
        
        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  className="pl-9"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {AVAILABLE_ROLES.map(role => (
                  <SelectItem key={role.id} value={role.id}>{role.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchUsers}>
              <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          
          {/* Users Table */}
          <div className="bg-white rounded-lg border">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roles</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {usersLoading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : users.map((user) => (
                  <tr key={user.user_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                          {user.avatar_url ? (
                            <img src={`${API_BASE_URL}${user.avatar_url}`} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-gray-600 font-medium text-sm">
                              {(user.first_name?.[0] || user.name?.[0] || user.email?.[0] || '?').toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">
                            {user.first_name && user.last_name 
                              ? `${user.first_name} ${user.last_name}`
                              : user.name || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">{user.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(user.roles?.length > 0 ? user.roles : [user.role]).map(role => (
                          <span key={role} className={`px-2 py-0.5 text-xs rounded-full ${getRoleBadgeColor(role)}`}>
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={user.is_active !== false ? 'success' : 'secondary'}>
                        {user.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {user.last_login_at 
                        ? new Date(user.last_login_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setViewUser(user)}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditUser(user)}>
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setSecurityUser(user)}>
                            <Shield className="w-4 h-4 mr-2" />
                            Security Settings
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleSendPasswordReset(user)}>
                            <Key className="w-4 h-4 mr-2" />
                            Send Password Reset
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleImpersonate(user)}>
                            <LogIn className="w-4 h-4 mr-2" />
                            Login as User
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleDeactivate(user)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Deactivate
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        
        {/* Invitations Tab */}
        <TabsContent value="invitations" className="mt-4">
          <div className="bg-white rounded-lg border">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Roles</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {invitationsLoading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" />
                    </td>
                  </tr>
                ) : invitations.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                      No pending invitations
                    </td>
                  </tr>
                ) : invitations.map((invitation) => (
                  <tr key={invitation.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{invitation.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {invitation.first_name} {invitation.last_name}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(invitation.roles || []).map(role => (
                          <span key={role} className={`px-2 py-0.5 text-xs rounded-full ${getRoleBadgeColor(role)}`}>
                            {role}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(invitation.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleResendInvitation(invitation)}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Resend
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600"
                          onClick={() => handleCancelInvitation(invitation)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Modals */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={() => {
          fetchUsers();
          fetchInvitations();
        }}
      />
      
      <EditUserModal
        user={editUser}
        open={!!editUser}
        onClose={() => setEditUser(null)}
        onSuccess={fetchUsers}
      />
      
      <SecuritySettingsModal
        user={securityUser}
        open={!!securityUser}
        onClose={() => setSecurityUser(null)}
      />
      
      {/* View User Details Modal */}
      <Dialog open={!!viewUser} onOpenChange={() => setViewUser(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              User Details
            </DialogTitle>
          </DialogHeader>
          
          {viewUser && (
            <div className="space-y-6">
              {/* Profile Section */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                  {viewUser.avatar_url || viewUser.picture ? (
                    <img 
                      src={viewUser.avatar_url || viewUser.picture} 
                      alt={viewUser.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold">{viewUser.name || viewUser.email}</h3>
                  <p className="text-gray-500">{viewUser.email}</p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant={viewUser.is_active ? 'default' : 'secondary'}>
                      {viewUser.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline">{viewUser.role}</Badge>
                    {viewUser.auth_provider && (
                      <Badge variant="outline" className="text-blue-600">
                        {viewUser.auth_provider === 'google' ? '🔗 Google' : '📧 Email'}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-1">User ID</p>
                  <p className="font-mono text-xs">{viewUser.user_id}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-1">First Name</p>
                  <p>{viewUser.first_name || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-1">Last Name</p>
                  <p>{viewUser.last_name || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-1">Phone</p>
                  <p>{viewUser.phone || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-1">Created</p>
                  <p>{viewUser.created_at ? new Date(viewUser.created_at).toLocaleDateString() : '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-gray-500 mb-1">Last Login</p>
                  <p>{viewUser.last_login_at ? new Date(viewUser.last_login_at).toLocaleString() : 'Never'}</p>
                </div>
              </div>
              
              {/* MFA Status */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-gray-500" />
                    <span className="font-medium">Two-Factor Authentication</span>
                  </div>
                  <Badge variant={viewUser.mfa_enabled ? 'default' : 'secondary'}>
                    {viewUser.mfa_enabled ? 'Enabled' : 'Not Enabled'}
                  </Badge>
                </div>
                {viewUser.mfa_enabled && viewUser.mfa_method && (
                  <p className="text-sm text-gray-500 mt-2">
                    Method: {viewUser.mfa_method === 'totp' ? 'Authenticator App' : 'Email OTP'}
                  </p>
                )}
              </div>
              
              {/* Security Restrictions Summary */}
              {viewUser.security_restrictions && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="h-5 w-5 text-gray-500" />
                    <span className="font-medium">Security Restrictions</span>
                  </div>
                  <div className="text-sm space-y-1">
                    {viewUser.security_restrictions.ip_whitelist_enabled && (
                      <p>✓ IP Whitelist enabled ({viewUser.security_restrictions.ip_whitelist?.length || 0} IPs)</p>
                    )}
                    {viewUser.security_restrictions.ip_blacklist_enabled && (
                      <p>✓ IP Blacklist enabled ({viewUser.security_restrictions.ip_blacklist?.length || 0} IPs)</p>
                    )}
                    {viewUser.security_restrictions.time_restriction?.enabled && (
                      <p>✓ Time restriction: {viewUser.security_restrictions.time_restriction.start_hour}:00 - {viewUser.security_restrictions.time_restriction.end_hour}:00</p>
                    )}
                    {viewUser.security_restrictions.day_restriction?.enabled && (
                      <p>✓ Day restriction enabled</p>
                    )}
                    {!viewUser.security_restrictions.ip_whitelist_enabled && 
                     !viewUser.security_restrictions.ip_blacklist_enabled &&
                     !viewUser.security_restrictions.time_restriction?.enabled &&
                     !viewUser.security_restrictions.day_restriction?.enabled && (
                      <p className="text-gray-400">No restrictions configured</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewUser(null)}>
              Close
            </Button>
            <Button onClick={() => { setEditUser(viewUser); setViewUser(null); }}>
              Edit User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSecuritySettings;
